<template>
  <div class="link-widget-panel scrollbar">
    <div class="measurements-container">
      <div class="measurements">
        <div class="form-group hidden position">
          <div class="form-row">
            <div class="input-group col">
              <div class="input-label">
                <span class="input-group-text">X</span>
              </div>
              <input
                type="number"
                class="form-control"
                id="link-x"
                v-model="xPosition"
                :disabled="isWidgetLocked"
              />
            </div>

            <div class="input-group col">
              <div class="input-label">
                <span class="input-group-text">Y</span>
              </div>
              <input
                type="number"
                class="form-control"
                v-model="yPosition"
                :disabled="isWidgetLocked"
              />
            </div>
          </div>
        </div>

        <div class="form-group hidden size">
          <div class="form-row">
            <div class="input-group col">
              <div class="input-label">
                <span class="input-group-text">W</span>
              </div>
              <input
                type="number"
                class="form-control"
                id="link-width"
                v-model="width"
                :disabled="isWidgetLocked"
              />
            </div>

            <div class="input-group col">
              <div class="input-label">
                <span class="input-group-text">H</span>
              </div>
              <input
                type="number"
                class="form-control"
                v-model="height"
                :disabled="isWidgetLocked"
              />
            </div>
          </div>
        </div>
      </div>

      <div class="extra-icons">
        <LockIcon v-show="isWidgetLocked" :locked="isWidgetLocked" />
        <VisibilityIcon :isVisible="isWidgetVisible" @clicked="updateVisibility" />
        <PixelIcon :usePixels="usePixels" @clicked="changeValuesMode" />
      </div>
    </div>

    <div class="form-group template-input name">
      <label for="template-name">Name</label>
      <input
        class="form-control"
        type="text"
        id="template-name"
        maxlength="40"
        placeholder="Name"
        :disabled="isWidgetLockedByOverride"
        v-model="value.object.name"
      />
    </div>

    <div class="form-group template-input link">
      <label for="template-link">Link to website</label>
      <input
        class="form-control"
        type="text"
        id="template-link"
        maxlength="260"
        placeholder="http://..."
        :disabled="isWidgetLockedByOverride"
        v-model="value.object.link"
      />
    </div>

    <div class="refresh-switch">
      <div class="floor-switch">
        <div class="custom-control custom-switch check">
          <input
            type="checkbox"
            class="custom-control-input"
            id="refresh-switch"
            v-model="value.object.refresh"
          />
          <label class="custom-control-label" for="refresh-switch"></label>
        </div>
      </div>

      <BaseText variant="inputLabel">{{
        value.object.refresh ? 'Refresh Every' : 'No Refresh'
      }}</BaseText>
    </div>

    <div class="form-group layout-input time" v-if="value.object.refresh">
      <div class="time-container">
        <input class="form-control" type="number" id="link-time" min="0" v-model="refreshTime" />

        <select
          class="custom-select"
          id="link-time-unit"
          :disabled="isWidgetLockedByOverride"
          v-model="value.object.timeUnit"
          style="zoom: 1.1; height: 38px"
        >
          <option :value="TIME_UNITS.SECONDS.value" style="zoom: 1.1">
            {{ TIME_UNITS.SECONDS.title }}
          </option>
          <option :value="TIME_UNITS.MINUTES.value" style="zoom: 1.1">
            {{ TIME_UNITS.MINUTES.title }}
          </option>
          <option default :value="TIME_UNITS.HOURS.value" style="zoom: 1.1">
            {{ TIME_UNITS.HOURS.title }}
          </option>
          <option :value="TIME_UNITS.DAYS.value" style="zoom: 1.1">
            {{ TIME_UNITS.DAYS.title }}
          </option>
        </select>
      </div>
    </div>

    <div class="form-group form-range layout-input">
      <div class="slider-top">
        <div class="form-label">Zoom</div>
        <div class="value">{{ value.object.zoom }}%</div>
      </div>
      <div class="slider-container">
        <BaseSlider :value="value.object.zoom" :min="25" :max="200" @update:value="updateZoom" />
      </div>
    </div>

    <div class="sub-section-title">Background</div>

    <div class="form-group hidden mb-1">
      <div class="form-row background-css">
        <color-picker
          class="col"
          input-el-id="layer-background-color"
          :disabled="isWidgetLockedByOverride"
          disableAlpha
          value="#FFFFFF"
          v-model="value.object.backgroundColor"
        />

        <div class="input-group col">
          <div class="input-label">
            <span class="input-group-text">
              <i class="material-icons-outlined align-middle">opacity</i>
            </span>
          </div>
          <input
            type="number"
            class="form-control"
            id="layer-opacity"
            min="0"
            max="100"
            :disabled="isWidgetLockedByOverride"
            v-model="value.object.opacity"
          />
        </div>
      </div>
    </div>
  </div>
</template>

<script>
  import ColorPicker from '@/components/ColorPicker';
  import BaseText from '@/components/common/ui/atoms/baseText/BaseText.vue';
  import PixelIcon from '@/components/layoutDesigner/editor/settingPanel/PixelIcon.vue';
  import LockIcon from '@/components/layoutDesigner/editor/settingPanel/LockIcon.vue';
  import VisibilityIcon from '@/components/layoutDesigner/editor/settingPanel/VisibilityIcon.vue';
  import BaseSlider from '@/components/common/ui/atoms/baseSlider/BaseSlider.vue';

  import widgetMixin from '@/components/templates/editor/settingPanel/widgetMixin';

  import { TIME_UNITS } from '@/constant/const';

  export default {
    name: 'LinkWidgetPanel',

    mixins: [widgetMixin],

    components: {
      ColorPicker,
      PixelIcon,
      LockIcon,
      VisibilityIcon,
      BaseText,
      BaseSlider,
    },

    data() {
      return {
        TIME_UNITS,
      };
    },

    computed: {
      refreshTime: {
        get() {
          return this.value.object.refreshTime || 0;
        },
        set(value = 0) {
          this.value.object.refreshTime = value;
          this.$emit('input', this.value);
        },
      },
    },

    methods: {
      updateZoom(value) {
        this.value.object.zoom = value;
        this.$emit('input', this.value);
      },
    },
  };
</script>

<style lang="scss" scoped>
  .link-widget-panel {
    height: 100%;
    overflow-y: auto;
    padding-right: 10px;
  }

  .sub-section-title {
    font-size: 14px;
    font-weight: 500;
    margin-bottom: 10px;
  }

  .time-container {
    display: flex;
    flex-direction: row;
    align-items: center;
    gap: 10px;
  }

  .refresh-switch {
    display: flex;
    margin-bottom: 8px;
  }
</style>
